import { brassDict } from './brassDict';

// Instrument "types" (the key a trumpet or tuba is built in).
//
// brassDict holds the fingerings for the standard instrument of each family:
// B♭ trumpet (read as a B♭ part) and BB♭ tuba (read in concert pitch).
// `offset` is how many semitones higher the type's open (no valves) note sits
// than the standard instrument's. The same note on the page is therefore
// fingered like the standard instrument's note `offset` semitones lower:
//   C tuba:  concert C2 is open, just like B♭1 on a BB♭ tuba (offset 2).
export const INSTRUMENT_TYPES = {
  trumpet: [
    { value: 'Bb', label: 'B♭ trumpet', offset: 0 },
    { value: 'C', label: 'C trumpet', offset: 2 },
    { value: 'D', label: 'D trumpet', offset: 4 },
    { value: 'Eb', label: 'E♭ trumpet', offset: 5 },
  ],
  tuba: [
    { value: 'BBb', label: 'BB♭ tuba', offset: 0 },
    { value: 'CC', label: 'CC tuba', offset: 2 },
    { value: 'Eb', label: 'E♭ tuba', offset: 5 },
    { value: 'F', label: 'F tuba', offset: 7 },
  ],
};

export const getTypes = (instrument) => INSTRUMENT_TYPES[instrument] || null;

export const getDefaultType = (instrument) => getTypes(instrument)?.[0].value ?? '';

const LETTERS = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

// 'Bb3' -> semitone number (so B#3 and C4 are the same pitch)
const toSemitone = (name) => {
  const m = /^([A-G])(#|b)?(\d)$/.exec(name);
  if (!m) return null;
  return LETTERS[m[1]] + (m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0) + 12 * Number(m[3]);
};

const pitchIndex = {};
const indexFor = (instrument) => {
  if (!pitchIndex[instrument]) {
    const map = {};
    Object.entries(brassDict[instrument] || {}).forEach(([name, combos]) => {
      const semitone = toSemitone(name);
      if (semitone !== null && !(semitone in map)) map[semitone] = combos;
    });
    pitchIndex[instrument] = map;
  }
  return pitchIndex[instrument];
};

const sameCombo = (a, b) => JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());

// A fourth valve takes the place of valves 1 + 3 (5 semitones lower), so
// every fingering that uses both can also be played with it instead.
const withFourthValve = (combos) => {
  const out = [...combos];
  combos.forEach((combo) => {
    if (combo.includes('j') && combo.includes('l')) {
      const alt = [...combo.filter((key) => key !== 'j' && key !== 'l'), 'f'];
      if (!out.some((existing) => sameCombo(existing, alt))) out.push(alt);
    }
  });
  return out;
};

/**
 * Accepted fingerings for `note` on `instrument`.
 * type: a value from INSTRUMENT_TYPES (ignored when the instrument has none).
 * fourthValve: euphonium only, adds the alternates that use the 'f' key.
 * Returns undefined when the note isn't playable on that type.
 */
export const getCombos = (instrument, note, type, fourthValve = false) => {
  const types = getTypes(instrument);
  const offset = types?.find((t) => t.value === type)?.offset ?? 0;

  let combos;
  if (offset === 0) {
    combos = brassDict[instrument]?.[note];
  } else {
    const semitone = toSemitone(note);
    combos = semitone === null ? undefined : indexFor(instrument)[semitone - offset];
  }
  if (!combos) return undefined;
  return instrument === 'euphonium' && fourthValve ? withFourthValve(combos) : combos;
};